import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import './Products.css';
import { ZapIcon } from '../ZapIcon';

const Products = () => {
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('agricultural');
  const shouldReduceMotion = useReducedMotion();

  // Products that should always show text first (flip behavior reversed)
  const flipExclusionList = [
    // Add product titles here that need reversed flip behavior
  ];

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCardInteraction = (cardId, hasImage) => {
    if (!hasImage) return;

    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  // Animation variants for better performance and consistency
  const cardVariants = {
    initial: { opacity: 0, y: 50 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        mass: 0.8
      }
    },
    hover: {
      y: isMobile ? 0 : -12,
      scale: isMobile ? 1 : 1.02,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    },
    tap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    }
  };

  const dissolveVariants = {
    front: {
      opacity: 1,
      scale: 1,
      transition: shouldReduceMotion ? { duration: 0.2 } : {
        duration: 0.2,
        ease: "easeInOut"
      }
    },
    back: {
      opacity: 0,
      scale: 0.95,
      transition: shouldReduceMotion ? { duration: 0.2 } : {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const imageVariants = {
    hidden: {
      opacity: 0,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: shouldReduceMotion ? { duration: 0.3, delay: 0.1 } : {
        duration: 0.3,
        delay: 0.1,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    initial: { scale: 1, rotate: 0 },
    hover: {
      scale: 1.15,
      rotate: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  const agriculturalProducts = [
    {
      title: 'Agriculture Sprayers',
      description: 'Comprehensive range of agricultural spraying solutions for farms of all sizes. From manual hand sprayers to motorized power sprayers, we provide efficient equipment for pesticide application, fertilizer spraying, and crop protection across diverse agricultural operations.',
      icon: <ZapIcon size={32} />,
      image: '/assets/Agriculture Sprayers.png'
    }
  ];

  // Tab configuration
  const tabs = [
    { id: 'agricultural', label: 'Agricultural Tools & Appliances', products: agriculturalProducts }
  ];

  const renderProductCard = (product, index, category) => {
    const cardId = `${category}-${index}`;
    const isDissolved = flippedCards.has(cardId);
    const hasImage = !!product.image;
    const isExcluded = flipExclusionList.includes(product.title);
    
    // Reverse flip logic for excluded products
    const shouldShowImage = isExcluded ? isDissolved : !isDissolved;

    return (
      <motion.div
        key={index}
        className={`product-card fade-in ${hasImage ? 'has-slider' : ''}`}
        variants={cardVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
        whileTap="tap"
        onClick={() => isMobile && handleCardInteraction(cardId, hasImage)}
        onMouseEnter={() => hasImage && !isMobile && setFlippedCards(prev => new Set(prev).add(cardId))}
        onMouseLeave={() => hasImage && !isMobile && setFlippedCards(prev => {
          const newSet = new Set(prev);
          newSet.delete(cardId);
          return newSet;
        })}
      >
        <div className="product-card-inner">
          {/* Front content - image or text based on exclusion list */}
          {hasImage ? (
            <>
              {/* Image side */}
              {shouldShowImage && (
                <motion.div
                  className="product-card-front"
                  animate={isDissolved ? "back" : "front"}
                  variants={dissolveVariants}
                >
                  <motion.img
                    src={product.image}
                    alt={product.title}
                    className="product-image"
                    initial={{ scale: 1, opacity: 1 }}
                    animate={{ scale: 1, opacity: 1 }}
                  />
                  <motion.div
                    className="product-image-overlay"
                    initial={{ opacity: 1, y: 0 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h4 className="product-image-title">{product.title}</h4>
                  </motion.div>
                </motion.div>
              )}
              
              {/* Text side */}
              <AnimatePresence>
                {!shouldShowImage && (
                  <motion.div
                    className="product-card-back"
                    variants={imageVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    <motion.div
                      className="product-icon-wrapper"
                      variants={iconVariants}
                      whileHover="hover"
                    >
                      <div className="product-icon">{product.icon}</div>
                    </motion.div>
                    <div className="product-content">
                      <h3 className="product-title">{product.title}</h3>
                      <p className="product-description">{product.description}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          ) : (
            <motion.div
              className="product-card-front"
              animate="front"
              variants={dissolveVariants}
            >
              <motion.div
                className="product-icon-wrapper"
                variants={iconVariants}
                whileHover="hover"
              >
                <div className="product-icon">{product.icon}</div>
              </motion.div>
              <div className="product-content">
                <h3 className="product-title">{product.title}</h3>
                <p className="product-description">{product.description}</p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <section id="products" className="products">
      <div className="container">
        <motion.div
          className="products-header"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="section-title">Our Other Products</h2>
          <p className="section-description">
            Expanding our portfolio beyond packaging with quality products for various industries
          </p>
        </motion.div>

        {/* Tabbed Product Categories */}
        <motion.div
          className="tabbed-categories"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="tabs-navigation">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              className="tab-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="products-grid"
                variants={{
                  animate: {
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.1
                    }
                  }
                }}
                initial="initial"
                animate="animate"
              >
                {tabs.find(tab => tab.id === activeTab)?.products.map((product, index) =>
                  renderProductCard(product, index, activeTab)
                )}
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Products;
